// Wie eine zusammengeführte Listenzeile (lib/uebergabe/listenzeilen.ts) angezeigt und
// bedient wird — zwei reine Entscheidungen, die beide ausschließlich von `zustand`
// (und für die Löschung zusätzlich `hatDatensatz`) abhängen und deshalb ohne
// Netz/DOM/IndexedDB testbar sind (Muster wie storno.ts/listenzeilen.ts).
//
// Praxis-Fund (Auftraggeber hat zwei Übergaben durchgespielt): Ein rein lokaler Entwurf
// im Zustand "wartet_auf_upload" (nie hochgeladen, z. B. weil beim Anlegen kein Netz
// bestand oder ein Termin versehentlich gestartet/abgebrochen wurde) ließ sich bisher
// NICHT entfernen — "Abbrechen" verlangt einen Datensatz (den es hier nie gab), das
// Verwerfen eines veralteten Entwurfs (entwurfVeraltet) setzt einen bereits
// abgeschlossenen/abgebrochenen Datensatz voraus. Die Zeile blieb dauerhaft als
// Warnzeile stehen. ermittleLoeschAktion() schließt genau diese Lücke, ohne den
// bestehenden, serverseitig geprüften Storno-Weg (abbrechenErfassung in
// UebergabeListe.tsx, lib/uebergabe/storno.ts) zu berühren oder zu umgehen.
//
// Zweiter Praxis-Fund: Die Liste zeigte den technischen Zustand ("Wartet auf Upload",
// "In Erfassung — anderes Gerät") als PRIMÄRES Merkmal. Für den Auftraggeber zählt an
// dieser Stelle nur: ist die Übergabe fertig oder nicht. istEntwurfsZustand() bündelt
// die drei noch-nicht-abgeschlossenen Zustände zu einem gemeinsamen "Entwurf"-Merkmal;
// geraeteHinweis() liefert den technischen Zustand als reinen Zusatztext dazu.
import type { Zeilenzustand } from './listenzeilen';

/**
 * Gilt diese Zeile aus Sicht des Auftraggebers als "noch nicht fertig" (= Entwurf)?
 * Abgeschlossen UND Abgebrochen sind eigene, endgültige Zustände (s. Migration
 * 2026-07-26-uebergabe-einfrieren.sql) und behalten ihr eigenes Abzeichen — alles
 * andere (auf diesem Gerät in Bearbeitung, wartet auf Upload, auf einem anderen Gerät
 * in Bearbeitung) ist ein Entwurf.
 *
 * Als Typprädikat deklariert, damit die Anzeige (UebergabeListe.tsx) im
 * false-Zweig automatisch auf 'abgeschlossen' | 'abgebrochen' verengt wird, statt eine
 * eigene, potenziell abweichende Fallunterscheidung zu pflegen.
 */
export function istEntwurfsZustand(
  zustand: Zeilenzustand,
): zustand is 'lokal_in_bearbeitung' | 'wartet_auf_upload' | 'in_erfassung_fremd' {
  return zustand === 'lokal_in_bearbeitung' || zustand === 'wartet_auf_upload' || zustand === 'in_erfassung_fremd';
}

// Zusatztext NEBEN dem "Entwurf"-Abzeichen — nur für die drei Entwurfs-Zustände
// gedacht (s. istEntwurfsZustand); für abgeschlossen/abgebrochen liefert
// geraeteHinweis() bewusst null, weil diese Zustände ihr eigenes, unverändertes
// Abzeichen behalten und den Zusatz nicht brauchen.
const GERAETE_HINWEIS: Partial<Record<Zeilenzustand, string>> = {
  lokal_in_bearbeitung: 'auf diesem Gerät',
  wartet_auf_upload: 'wartet auf Upload',
  in_erfassung_fremd: 'auf einem anderen Gerät',
};

/** Technischer Zusatztext zu einem Entwurfs-Zustand, oder null (abgeschlossen/abgebrochen). */
export function geraeteHinweis(zustand: Zeilenzustand): string | null {
  return GERAETE_HINWEIS[zustand] ?? null;
}

/**
 * * `gesperrt` — abgeschlossen (bewusst unlöschbar) oder abgebrochen (bereits
 *   Endzustand; ein evtl. noch auf diesem Gerät liegender, verwaister lokaler Entwurf
 *   hat einen eigenen, unabhängigen Weg — "Lokalen Entwurf verwerfen", s.
 *   entwurfVeraltet in listenzeilen.ts — und läuft nicht über diese Funktion).
 * * `server_stornieren` — es gibt einen Datensatz (in_erfassung): MUSS über die
 *   vorhandene, serverseitig geprüfte Storno-Logik laufen (pruefeStornoErlaubt +
 *   holeServerStatus, s. abbrechenErfassung in UebergabeListe.tsx) — der Server hat
 *   das letzte Wort, ein Client-Delete allein würde die Prüfung umgehen.
 * * `lokal_loeschen` — reiner lokaler Entwurf OHNE jeden Datensatz
 *   ("wartet_auf_upload"): serverseitig gibt es nichts zu prüfen oder zu markieren,
 *   der Entwurf wird schlicht von diesem Gerät entfernt.
 */
export type LoeschAktion = 'gesperrt' | 'server_stornieren' | 'lokal_loeschen';

export function ermittleLoeschAktion(zeile: { zustand: Zeilenzustand; hatDatensatz: boolean }): LoeschAktion {
  if (zeile.zustand === 'abgeschlossen' || zeile.zustand === 'abgebrochen') return 'gesperrt';
  return zeile.hatDatensatz ? 'server_stornieren' : 'lokal_loeschen';
}
